import type { BackendFilter } from '../../data-views/services/dynamicTablesService';
import type {
  DashboardCalculation,
  DashboardFilter,
  DashboardFilterOperator,
  DashboardSourceColumn,
  DashboardWidgetType,
} from '../types';
import { filterTakesValue, isFilterComplete } from '../widgetFilters';

/**
 * The wire shapes of `POST /dashboards/widgets/data`, and the translation from
 * this feature's model into them (SL-1665).
 *
 * ⚠️ **The endpoint exists and is deployed** — `DynamicTables/application/
 * Dashboards/post/postWidgetData.ts`, whose own header says it is built to map
 * 1:1 onto this panel. So unlike `dashboardsService`, this file is not a
 * proposed contract: it is *their* contract, restated on this side, and it must
 * follow the backend rather than the other way round.
 *
 * It is a separate module from `types.ts` on purpose. What the draft holds and
 * what travels over the wire are two vocabularies that disagree in three ways,
 * and every one of those disagreements is a decision someone made for a reason:
 *
 * 1. **Filters travel by column *name*, not by id.** `RowFilterI.column` is a
 *    name. The draft stores `columnId`, because the PRD requires columns to be
 *    referenced by identifier so renaming one does not break the widget
 *    (Feature 6). Both rules stand; the resolution happens here, at the last
 *    moment, against the schema that was just read.
 * 2. **The backend's vocabulary is snake_case** (`is_not`, `greater_than`),
 *    this feature's is camelCase, and the two sets are not the same size.
 * 3. **Some of what the panel offers has nowhere to go**, and some of what the
 *    backend accepts the panel does not offer. Both directions are listed
 *    below, by name, rather than discovered at runtime.
 *
 * Keeping the mapping in one module is what stops a caller from building a
 * request by hand and drifting — the same reason `tableSchemaService` is the
 * only place the tables' type vocabulary is read.
 */

/** Comparisons the endpoint accepts (`FilterOperator`, tables.types.ts). */
export type WireFilterOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'does_not_contain'
  | 'starts_with'
  | 'greater_than'
  | 'greater_than_or_equal'
  | 'less_than'
  | 'less_than_or_equal'
  | 'is_between'
  | 'is_any_of'
  | 'is_empty'
  | 'is_not_empty';

/** One filter as the endpoint reads it (`RowFilterI`, tables.types.ts). */
export interface WireFilter {
  /** The column's **name**. See the note above. */
  column: string;
  op: WireFilterOperator;
  value?: unknown;
  /** Only for `is_between`, which the backend rejects without it. */
  valueTo?: unknown;
  /** Only for `is_any_of`, which the backend rejects without a non-empty array. */
  values?: unknown[];
  /**
   * The connected table the column belongs to (ADR-047 point 5, SL-1906).
   * Absent for the main table; `column` is then a name of **that** table.
   */
  tableId?: string;
}

/**
 * A value of the reader's filter bar, translated for the endpoint (SL-1710).
 *
 * The data views' own filter shape plus the table it belongs to: absent for the
 * main table, present for a connected one (SL-1792), where `column` is a name of
 * **that** table — the same rule as `WireFilter.tableId`.
 */
export type ReaderFilter = BackendFilter & { tableId?: string };

/**
 * This feature's operators, in the backend's words.
 *
 * ⚠️ `endsWith` is **absent, and that is the finding**: the panel offers "ends
 * with" over text columns and the endpoint has no operator for it. A filter
 * using it cannot be sent, and this module says so out loud (see
 * `toWireFilter`) rather than quietly dropping the filter — a widget that
 * silently reads more rows than the user asked for is worse than one that
 * refuses to load.
 *
 * TODO(backlog): take `ends_with` to backend, or withdraw it from
 * `widgetFilters.ts`. Until one of the two, it is a dead end in the UI. Left
 * SL-1665: it needs a backend decision, and the ticket does not touch filters'
 * wire contract.
 */
const WIRE_OPERATORS: Partial<Record<DashboardFilterOperator, WireFilterOperator>> = {
  is: 'is',
  isNot: 'is_not',
  contains: 'contains',
  startsWith: 'starts_with',
  gt: 'greater_than',
  gte: 'greater_than_or_equal',
  lt: 'less_than',
  lte: 'less_than_or_equal',
  empty: 'is_empty',
  notEmpty: 'is_not_empty',
};

/**
 * What the endpoint accepts and this panel does not offer.
 *
 * Listed rather than left out, because each is a feature decision waiting to be
 * made and not an oversight:
 *
 * - `does_not_contain` — the negation of a comparison the panel does offer.
 * - `is_between` — a range over numbers and dates. Would need a second value
 *   box, and `DashboardFilter` would need a third branch carrying the pair.
 * - `is_any_of` — several options of a `choice` column at once. Would turn that
 *   kind's value control into a multiple `Select`.
 *
 * TODO(backlog): decide with product which of the three the panel should offer.
 * Left SL-1665: a product call about new filter operators, outside its scope.
 */
export const UNOFFERED_WIRE_OPERATORS: WireFilterOperator[] = [
  'does_not_contain',
  'is_between',
  'is_any_of',
];

/** Why a filter could not be put on the wire. Codes, never sentences. */
export type WireFilterRefusal = 'unknownColumn' | 'unsupportedOperator';

export type WireFilterResult =
  | { ok: true; filter: WireFilter }
  | { ok: false; reason: WireFilterRefusal; filter: DashboardFilter };

/**
 * One filter, translated for the endpoint.
 *
 * Returns a result rather than throwing or returning `undefined`, so a caller
 * cannot drop a filter by accident: **a filter that does not travel widens the
 * widget's data**, which is the failure nobody notices. The caller decides what
 * to do about it; what it may not do is not know.
 *
 * `columns` is the schema just read for the table — the resolution of id to
 * name happens against it, here, and not at the time the filter was written.
 */
export function toWireFilter(
  filter: DashboardFilter,
  columns: Map<string, DashboardSourceColumn>,
): WireFilterResult {
  const column = columns.get(filter.columnId);
  // A connected column travels with its table, and its name is resolved against
  // that table's schema — the caller hands in that one (SL-1792).
  const table = filter.tableId ? { tableId: filter.tableId } : {};
  // A column that was deleted from the table since the filter was written. Its
  // name cannot be resolved, so the filter cannot be honoured.
  if (!column) return { ok: false, reason: 'unknownColumn', filter };

  const op = WIRE_OPERATORS[filter.operator];
  if (!op) return { ok: false, reason: 'unsupportedOperator', filter };

  return {
    ok: true,
    filter: filterTakesValue(filter)
      ? { column: column.name, op, value: filter.value, ...table }
      : // No `value` key at all rather than `undefined`: the backend reads
        // presence, and an explicit undefined serialises to a key that is there.
        { column: column.name, op, ...table },
  };
}

/**
 * Every filter of a widget, translated, with whatever could not travel.
 *
 * Both halves are returned together because the caller needs both: the wire
 * filters to send, and the refusals to say so. Sending the first without
 * surfacing the second is how a widget quietly shows the wrong rows.
 *
 * `connectedColumns` are the schemas of the connected tables, by table id: a
 * filter over a connected column is named from its own table (SL-1792).
 */
export function toWireFilters(
  filters: DashboardFilter[],
  columns: DashboardSourceColumn[],
  connectedColumns: Record<string, DashboardSourceColumn[]> = {},
): { wire: WireFilter[]; refused: Extract<WireFilterResult, { ok: false }>[] } {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const byTable = new Map(
    Object.entries(connectedColumns).map(([tableId, list]) => [
      tableId,
      new Map(list.map((column) => [column.id, column])),
    ]),
  );
  // A filter still waiting for its value narrows nothing (SL-1798): it stays
  // out of the request, neither sent nor reported as refused — the panel and
  // the card already say "un filtro sin valor". Sent, `contains ""` emptied the
  // widget instead.
  const results = filters
    .filter(isFilterComplete)
    .map((filter) =>
      toWireFilter(filter, filter.tableId ? (byTable.get(filter.tableId) ?? new Map()) : byId),
    );

  return {
    wire: results.flatMap((result) => (result.ok ? [result.filter] : [])),
    refused: results.filter(
      (result): result is Extract<WireFilterResult, { ok: false }> => !result.ok,
    ),
  };
}

// ---------------------------------------------------------------------------
// Calculations
// ---------------------------------------------------------------------------

/**
 * The aggregations the endpoint computes (`KpiKind`, tables.types.ts).
 *
 * One vocabulary shared with the KPI cards of Feature 6 — the backend reuses it
 * deliberately rather than inventing a second aggregation enum for widgets.
 */
export type WireCalculation =
  | 'count'
  | 'filled'
  | 'empty'
  | 'unique'
  | 'percent_filled'
  | 'percent_empty'
  | 'sum'
  | 'avg'
  | 'median'
  | 'min'
  | 'max'
  | 'p95'
  | 'earliest'
  | 'latest';

/**
 * This feature's calculations, in the backend's words.
 *
 * The other direction: the endpoint also computes `earliest` and `latest` —
 * first and last date of a column — which this feature never offers. They are
 * the natural measures over a date column, and the catalogue has no equivalent.
 *
 * TODO(backlog): consider adding them to `DashboardCalculation`. Left SL-1665:
 * new calculations are a catalogue change the ticket does not ask for.
 */
const WIRE_CALCULATIONS: Partial<Record<DashboardCalculation, WireCalculation>> = {
  count: 'count',
  countDistinct: 'unique',
  filled: 'filled',
  empty: 'empty',
  percentFilled: 'percent_filled',
  percentEmpty: 'percent_empty',
  sum: 'sum',
  average: 'avg',
  min: 'min',
  max: 'max',
  median: 'median',
  p95: 'p95',
};

/** Calculations the endpoint computes and this feature does not offer. */
export const UNOFFERED_WIRE_CALCULATIONS: WireCalculation[] = ['earliest', 'latest'];

/**
 * One calculation, in the backend's words, or `null` when it has none.
 *
 * `null` and not a fallback: substituting, say, `avg` for `median` would draw a
 * number that is not the one asked for, which is worse than drawing nothing.
 */
export const toWireCalculation = (calculation: DashboardCalculation): WireCalculation | null =>
  WIRE_CALCULATIONS[calculation] ?? null;

// ---------------------------------------------------------------------------
// Widget types
// ---------------------------------------------------------------------------

/**
 * This feature's visualisations, in the backend's words (`WidgetChartType`).
 *
 * Only the spelling differs — the endpoint's list and this feature's thirteen
 * types cover the same shapes, in snake_case where ours are camelCase. The
 * funnel joined the endpoint in SL-1825 (`FunnelWidgetResponse`), so every type
 * of Apéndice A now has a chart type to ask for.
 */
const WIRE_CHART_TYPES: Partial<Record<DashboardWidgetType, string>> = {
  kpi: 'kpi',
  line: 'line',
  area: 'area',
  bar: 'bar',
  horizontalBar: 'horizontal_bar',
  stackedBar: 'stacked_bar',
  stackedHorizontalBar: 'stacked_h_bar',
  pie: 'pie',
  donut: 'donut',
  scatter: 'scatter',
  matrix: 'matrix',
  table: 'table',
  funnel: 'funnel',
};

/** The chart type the endpoint knows this visualisation by, or `null`. */
export const toWireChartType = (type: DashboardWidgetType): string | null =>
  WIRE_CHART_TYPES[type] ?? null;

/**
 * Most measures one `tooltips` list may carry (`MAX_TOOLTIP_MEASURES`,
 * widgetData.types.ts — SL-1824). One more is a 422, so the Tooltips well never
 * holds more than this.
 */
export const MAX_TOOLTIP_MEASURES = 10;

// ---------------------------------------------------------------------------
// Reading the backend's words back
// ---------------------------------------------------------------------------

/**
 * The three tables above, inverted.
 *
 * They live here and not in `dashboardContract.ts` because the tables do: a
 * second module deriving its own inverse is a second place that has to be
 * updated when a new word lands, and the one that is forgotten
 * is the one that silently stops round-tripping.
 *
 * Built from the forward tables rather than written out, so an entry cannot
 * exist in one direction and not the other.
 *
 * `invert` is exported for the same reason: `dashboardContract.ts` keeps its own
 * storage-only tables and needs their inverses too. A second module rolling its
 * own inversion is a second place to update — which is the very thing this note
 * warns about.
 */
export const invert = <K extends string, V extends string>(
  table: Partial<Record<K, V>>,
): Partial<Record<V, K>> =>
  Object.fromEntries(Object.entries(table).map(([key, value]) => [value, key])) as Partial<
    Record<V, K>
  >;

const OUR_OPERATORS = invert(WIRE_OPERATORS);
const OUR_CALCULATIONS = invert(WIRE_CALCULATIONS);
const OUR_CHART_TYPES = invert(WIRE_CHART_TYPES);

/** The operator this feature knows a wire operator by, or `null`. */
export const fromWireOperator = (op: WireFilterOperator): DashboardFilterOperator | null =>
  OUR_OPERATORS[op] ?? null;

/** The calculation this feature knows a wire aggregation by, or `null`. */
export const fromWireCalculation = (kind: WireCalculation): DashboardCalculation | null =>
  OUR_CALCULATIONS[kind] ?? null;

/** The visualisation this feature knows a wire chart type by, or `null`. */
export const fromWireChartType = (chartType: string): DashboardWidgetType | null =>
  OUR_CHART_TYPES[chartType] ?? null;

// ---------------------------------------------------------------------------
// Join warnings
// ---------------------------------------------------------------------------

/**
 * Something the join of a connected table had to settle on its own (ADR-047
 * point 4).
 *
 * Two things, and one entry can carry both: the join column repeats a value
 * (the backend keeps the row with the lowest `rowNumber`, so the number may be
 * attributed to the "wrong" duplicate), or the connected table passed the row
 * cap and was read only up to it (rows past it never match).
 */
export interface JoinWarning {
  tableId: string;
  connectedColumnId: string;
  /** How many distinct values repeat. `0` when the warning is only `truncated`. */
  duplicateKeys: number;
  /** The connected table was read only up to the row cap. */
  truncated: boolean;
}

/**
 * The payload's `joinWarnings`, read defensively.
 *
 * `duplicateKeys` is accepted as a count or as the list of repeated values, so
 * the card says the right number whichever way SL-1906 lands. An entry with
 * no repeated key is kept when it says `truncated` — the backend sends that one
 * on its own. Anything else is dropped: a malformed warning must not take the
 * chart down with it.
 */
export function joinWarningsOf(payload: Record<string, unknown>): JoinWarning[] {
  const raw = payload.joinWarnings;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): JoinWarning[] => {
    if (!entry || typeof entry !== 'object') return [];
    const { tableId, connectedColumnId, duplicateKeys, truncated } = entry as Record<
      string,
      unknown
    >;
    if (typeof tableId !== 'string' || typeof connectedColumnId !== 'string') return [];
    const count = Array.isArray(duplicateKeys)
      ? duplicateKeys.length
      : typeof duplicateKeys === 'number'
        ? duplicateKeys
        : 0;
    const cut = truncated === true;
    return count > 0 || cut
      ? [{ tableId, connectedColumnId, duplicateKeys: count, truncated: cut }]
      : [];
  });
}
